import { Link } from "@tanstack/react-router";
import { Fuel, Gauge, MapPin, Star, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Car } from "@/data/cars";
import { CarPhoto } from "@/components/site/CarPhoto";
import { cn } from "@/lib/utils";

export function CarCard({ car, view = "grid" }: { car: Car; view?: "grid" | "list" }) {
  const specs = (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <Gauge className="size-3.5 text-gold" /> {car.transmission}
      </span>
      <span className="flex items-center gap-1.5">
        <Fuel className="size-3.5 text-gold" /> {car.fuelType}
      </span>
      <span className="flex items-center gap-1.5">
        <Users className="size-3.5 text-gold" /> {car.seats} seats
      </span>
    </div>
  );

  return (
    <article
      className={cn(
        "group overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:border-gold/45 hover:shadow-lift",
        view === "list" && "sm:grid sm:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]",
      )}

    >
      <Link
        to="/rentals/$slug"
        params={{ slug: car.slug }}
        className="photo-stage relative block overflow-hidden"
      >
        <CarPhoto
          src={car.images[0]}
          alt={`${car.brand} ${car.name.split(":")[0]} for rent in Bucharest`}
          className="aspect-[3/2] w-full p-4 transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_35%,transparent_35%,oklch(0_0_0/0.55)_100%)]" />
        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-card via-card/60 to-transparent" />
        <Badge className="absolute left-3 top-3 border border-gold/40 bg-ink/80 text-xs uppercase tracking-[0.14em] text-gold backdrop-blur-sm">
          {car.brand}
        </Badge>
        <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full border border-gold/30 bg-ink/80 px-2.5 py-1 text-xs font-semibold shadow-card backdrop-blur-sm">
          <Star className="size-3.5 fill-gold text-gold" /> {car.rating.toFixed(1)}
        </span>

      </Link>


      <div className="flex flex-col gap-3 p-5">
        <div className="min-w-0">
          <h3 className="line-clamp-2 font-display text-base font-bold leading-snug">
            <Link to="/rentals/$slug" params={{ slug: car.slug }} className="hover:text-gold">
              {car.name}
            </Link>
          </h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="size-3.5 shrink-0 text-gold" />
            <span className="truncate">{car.location}</span>
          </p>
        </div>

        {specs}

        {view === "list" && (
          <p className="line-clamp-2 text-sm text-muted-foreground">{car.description}</p>
        )}

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-4">
          <p className="font-display text-lg font-bold">
            €{car.pricePerDay}
            <span className="text-xs font-medium text-muted-foreground"> / Day</span>
          </p>
          <Button asChild variant="gold" size="sm">
            <Link
              to="/rentals/$slug"
              params={{ slug: car.slug }}
              aria-label={`Rent Now — ${car.name}`}
            >
              Rent Now
            </Link>
          </Button>
        </div>
      </div>
    </article>
  );
}
